// State를 기준으로 테이블

import TooltipComponent from 'components/broadcast/Tooltop'
import { Button, Checkbox, LucideIcon, Table } from 'components/shared'
import dayjs from 'dayjs'
import { ShippingType } from 'interfaces/broadcast.interface'
import { IDetailOrder, IDetailTodo, Payment } from 'interfaces/live.interface'
import {
    EditActionType,
    OrderStatusChangeKr,
    OrderStatusWithTotal,
    StatusTableProps,
    TableRow,
} from 'interfaces/toss.interface'
import GuestIdCopy from '../GuestIdCopy'
import { useMemo, useState } from 'react'
import { classNameJoin, numbericComma, phoneHypen } from 'utils/lib'
import { getFieldsByStatus, getShippingFee, statusColumnKeys } from 'utils/tossDetailHandler'
import { matchTossErrorCode, TossErrorCodeToMessage } from 'utils/TossErrorCodeToMessage'
import { mapTodosToRows } from 'utils/tossOrder'

interface IField {
    name: string
    column: string
    width?: number
    align?: string
    overflow?: boolean
    render?: (
        item: Record<string, any>,
        rowIndex: number,
        original?: {
            status: OrderStatusWithTotal
            todo: IDetailTodo
            order: IDetailOrder
            broadcastId: string
            shippingInfo: {
                shippingCharge: number | null
                shippingPurchaseAmount: number | null
                shippingType: ShippingType
            }
            handleRowAction: (action: EditActionType, data: IDetailTodo | IDetailOrder) => void
            selectedOrders: Number[]
        }
    ) => React.ReactNode
}

export const baseFields: Record<string, IField> = {
    checkbox: {
        name: '',
        column: 'checkbox',
        width: 40,
        render: (items, _, original) => {
            const isAllSelected = original?.todo.orders.every((order) =>
                original?.selectedOrders.includes(order.id)
            )
            // console.log(isAllSelected)

            return (
                <label className="flex cursor-pointer select-none w-full h-full justify-center">
                    <Checkbox
                        checked={isAllSelected}
                        onChange={(e) => {
                            if (e.target.checked) {
                                original?.handleRowAction('checkbox-user-add', original?.todo)
                            } else {
                                original?.handleRowAction('checkbox-user-remove', original?.todo)
                            }
                            console.log('checkbox', e.target.checked)
                        }}
                    />
                </label>
            )
        },
    },
    orderId: { name: '주문번호', column: 'orderId', width: 142 },
    nickname: {
        name: '주문자 닉네임',
        column: 'nickname',
        width: 255,
        render: (item) => <p className="break-words">{item.nickname}</p>,
    },
    guestId: {
        name: '유저 식별번호',
        column: 'guestId',
        width: 120,
        render: (item) => {
            return <GuestIdCopy guestId={item.guestId} />
        },
    },
    paidAt: {
        name: '결제완료일시',
        column: 'paidAt',
        width: 142,
        render: (item) => {
            const paidAt = item.paidAt
                ? dayjs(item.paidAt).format('YYYY.MM.DD HH:mm:ss')
                : dayjs(item.createdAt).format('YYYY.MM.DD HH:mm:ss')
            return renderChangedByInfo(paidAt, item.changedByNickname)
        },
    },
    deliveryReadyAt: {
        name: '배송준비일시',
        column: 'deliveryReadyAt',
        width: 142,
        render: (item) => {
            const deliveryReadyAt = item.deliveryReadyAt
                ? dayjs(item.deliveryReadyAt).format('YYYY.MM.DD HH:mm:ss')
                : ''
            return renderChangedByInfo(deliveryReadyAt, item.changedByNickname)
        },
    },
    deliveredAt: {
        name: '배송완료일시',
        width: 142,
        column: 'deliveredAt',
        render: (item) => {
            const deliveredAt = item.deliveredAt
                ? dayjs(item.deliveredAt).format('YYYY.MM.DD HH:mm:ss')
                : ''
            return renderChangedByInfo(deliveredAt, item.changedByNickname)
        },
    },
    productName: {
        name: '상품명',
        column: 'productName',
        width: 255,
        render: (item, _, original) => (
            <label
                className={classNameJoin([
                    'flex flex-row items-center gap-2 ',
                    original?.status === 'REQUEST' ||
                    original?.status === 'COMPLETE' ||
                    original?.status === 'DELIVERY_READY' ||
                    original?.status === 'DELIVERY' ||
                    original?.status === 'CANCEL'
                        ? 'cursor-pointer'
                        : '',
                ])}
            >
                {(original?.status === 'REQUEST' ||
                    original?.status === 'COMPLETE' ||
                    original?.status === 'DELIVERY_READY' ||
                    original?.status === 'DELIVERY' ||
                    original?.status === 'CANCEL') && (
                    <Checkbox
                        checked={original?.selectedOrders.includes(original?.order.id)}
                        onChange={(e) => {
                            if (e.target.checked) {
                                original?.handleRowAction(
                                    'checkbox-item-add',
                                    original.order as IDetailOrder
                                )
                            } else {
                                original?.handleRowAction(
                                    'checkbox-item-remove',
                                    original.order as IDetailOrder
                                )
                            }
                        }}
                    />
                )}
                <p className="break-words">{item.productName}</p>
            </label>
        ),
    },
    productId: { name: '상품번호', column: 'productId', width: 142 },
    option: {
        name: '옵션',
        column: 'option',
        render: (item, _, original) => {
            const status = original?.status
            const text = !item.option || item.option === '-' ? '' : item.option
            if (
                status === 'CANCEL' ||
                status === 'TOTAL' ||
                status === 'REFUND_HOLD_CANCEL' ||
                status === 'REFUND_HOLD_RETURN' ||
                status === 'REFUND'
            ) {
                return <p className="">{text}</p>
            }
            return (
                <button
                    type="button"
                    className="flex flex-row items-center gap-1"
                    onClick={() => {
                        original &&
                            original.handleRowAction('editOption', {
                                ...original?.order,
                                orderCode: original.todo.orders[0].orderCode,
                            })
                        // console.log(item)
                    }}
                >
                    <p className="text-left">{text}</p>
                    <LucideIcon name="Pencil" size={12} className="text-[#007AFF] min-w-[16px]" />
                </button>
            )
        },
    },
    quantity: {
        name: '수량',
        column: 'quantity',
        width: 142,
        render: (item, _, original) => {
            const status = original?.status
            let isShowEdit = true
            if (
                status === 'COMPLETE' ||
                status === 'DELIVERY_READY' ||
                status === 'DELIVERY' ||
                status === 'CANCEL' ||
                status === 'REFUND_HOLD_CANCEL' ||
                status === 'REFUND_HOLD_RETURN' ||
                status === 'REFUND' ||
                status === 'TOTAL'
            ) {
                isShowEdit = false
            }

            if (isShowEdit) {
                return (
                    <button
                        type="button"
                        className="flex flex-row items-center gap-1 pr-2"
                        onClick={() => {
                            original &&
                                original.handleRowAction('editQuantity', {
                                    ...original?.order,
                                    orderCode: original.todo.orders[0].orderCode,
                                })
                        }}
                    >
                        <p className="">{numbericComma(item.quantity ?? 0)}</p>
                        <LucideIcon
                            name="Pencil"
                            size={12}
                            className="text-[#007AFF] min-w-[16px]"
                        />
                    </button>
                )
            } else {
                return <p className="">{numbericComma(item.quantity ?? 0)}</p>
            }
        },
    },
    price: {
        name: '판매가',
        width: 142,
        column: 'price',
        render: (item) => numbericComma(item.price ?? 0),
    },
    totalPrice: {
        name: '총상품금액',
        width: 142,
        column: 'totalPrice',
        render: (item, _, original) => {
            // 수량 변경에 따른 총 상품 금액 구하기
            const cnt = original?.order.count ?? 0
            const price = original?.order.price ?? 0
            const totalPrice = cnt * price
            // const totalPrice = original?.todo.totalPayment ?? 0
            return numbericComma(totalPrice)
        },
    },
    shippingFee: {
        name: '배송비',
        width: 142,
        column: 'shippingFee',
        render: (item, __, original) => {
            const status = original?.status

            let fee = 0

            // 취소/반품의 경우 order 내부의 shippingFee를 사용
            if (
                status === 'CANCEL' ||
                status === 'REFUND_HOLD_RETURN' ||
                status === 'REFUND_HOLD_CANCEL' ||
                status === 'REFUND'
            ) {
                fee = original?.order.shippingFee ?? 0
            } else {
                // 주문 상태가 TOTAL이 아닌 경우, todo의 shippingInfo를 사용
                fee = getShippingFee({
                    shippingType: original?.shippingInfo.shippingType ?? 'PAID_SHIPPING',
                    shippingCharge: original?.shippingInfo.shippingCharge ?? 0,
                    shippingPurchaseAmount: original?.shippingInfo.shippingPurchaseAmount ?? 0,
                    totalProductPrice: Number(original?.todo.totalPayment ?? 0),
                    remoteAreaShippingCharge: original?.todo.remoteAreaShippingCharge ?? 0,
                    jejuShippingCharge: original?.todo.jejuShippingCharge ?? 0,
                    isTotal: false,
                })
            }

            // console.log(totalProductPrice, fee)
            return numbericComma(fee)
        },
    },
    remoteFee: {
        name: '제주/도서산간 배송비',
        column: 'remoteFee',
        width: 142,
        render: (item, _, original) => {
            const status = original?.status

            let fee = 0

            // 취소/반품의 경우 order 내부의 shippingFee를 사용
            if (
                status === 'CANCEL' ||
                status === 'REFUND_HOLD_RETURN' ||
                status === 'REFUND_HOLD_CANCEL' ||
                status === 'REFUND'
            ) {
                fee = original?.order.additionalShippingFee ?? 0
            } else {
                fee =
                    (original?.todo.jejuShippingCharge ?? 0) +
                    (original?.todo.remoteAreaShippingCharge ?? 0)
            }
            return <>{numbericComma(fee)}</>
        },
    },

    paymentMethod: {
        name: '결제수단',
        column: 'paymentMethod',
        width: 142,
        render: (item, _, original) => {
            const __ = original?.todo.payment as Payment | null
            return <>{__?.method}</>
        },
    },
    refundMethod: { name: '환불방식', column: 'refundMethod', width: 142 },
    refundType: { name: '환불유형', column: 'refundType', width: 142 },
    refundAmount: {
        name: '상품금액',
        column: 'refundAmount',
        width: 142,
        render: (item, _, original) => {
            const price = (original?.order.price ?? 0) * (original?.order.count ?? 0)
            return numbericComma(price)
        },
    },
    receiverName: { name: '수령인이름', column: 'receiverName', width: 142 },
    receiverPhone: {
        name: '수령인연락처',
        column: 'receiverPhone',
        render: (item) => phoneHypen(item.receiverPhone ?? ''),
        width: 142,
    },
    address1: {
        name: '수령인주소',
        column: 'address1',

        render: (item, _, original) => {
            const status = original?.status
            let fullAddress = ''
            let isShowHistory = false
            if (item.zipcode) {
                fullAddress = `(${item.zipcode}) ${item.address1} ${item.address2}`
                isShowHistory = true
            } else {
                fullAddress = `${item.address2}`
            }
            if (status === 'TOTAL') {
                return <p className="">{fullAddress ?? ' '}</p>
            }
            return (
                <div className="flex flex-col gap-1">
                    <button
                        type="button"
                        className="flex flex-row items-center gap-1 pr-2"
                        onClick={() => {
                            original && original.handleRowAction('editAddress', original.todo)
                        }}
                    >
                        <p className="text-left">{fullAddress ?? ' '}</p>
                        <LucideIcon
                            name="Pencil"
                            size={12}
                            className="text-[#007AFF] min-w-[16px]"
                        />
                    </button>
                    {isShowHistory && (
                        <button
                            className="w-fit py-[2px] px-1 border border-secondary-900 text-secondary-500 text-xs"
                            onClick={() => {
                                original?.handleRowAction(
                                    'address-history',
                                    original.todo as IDetailTodo
                                )
                                // handleAddressHistoryClick(item.guestId, item.nickname)
                            }}
                        >
                            변경이력
                        </button>
                    )}
                </div>
            )
        },
    },
    // address2: { name: '상세주소', column: 'address2', width: 240 },
    // zipcode: { name: '우편번호', column: 'zipcode', width: 240 },
    ordererPhone: {
        name: '주문자연락처',
        column: 'ordererPhone',
        render: (item) => phoneHypen(item.ordererPhone ?? ''),
        width: 142,
    },
    memo: { name: '요청사항', column: 'memo', width: 240 },
    cancelFailureReason: {
        name: '환불실패사유',
        column: 'cancelFailureReason',
        width: 142,
        render: (item) => {
            return item.cancelFailureReason ? (
                <div className="relative group cursor-pointer w-fit">
                    <p className="text-[#007AFF]">보기</p>
                    <p className="hidden absolute top-0 translate-y-[20px] p-4 bg-black bg-opacity-80 rounded-[8px] text-[#fefefe] whitespace-pre-wrap break-words z-10 opacity-0 group-hover:opacity-100 transition-opacity max-w-[300px] w-max group-hover:block">
                        {matchTossErrorCode(item.cancelFailureReason)}
                    </p>
                </div>
            ) : (
                ''
            )
        },
    },
    cancelReason: { name: '취소사유', column: 'cancelReason', width: 142 },
    cancelDetailReason: {
        name: '취소상세사유',
        column: 'cancelDetailReason',
        width: 142,
        render: (item, _, original) => {
            return original?.order.cancelReasonDetail ? (
                <div className="relative group cursor-pointer w-fit">
                    <p className="text-[#007AFF]">보기</p>
                    <p className="hidden absolute top-0 translate-y-[20px] p-4 bg-black bg-opacity-80 rounded-[8px] text-[#fefefe] whitespace-pre-wrap break-words z-10 opacity-0 group-hover:opacity-100 transition-opacity max-w-[300px] w-max group-hover:block">
                        {original?.order.cancelReasonDetail}
                    </p>
                </div>
            ) : (
                ''
            )
        },
    },
    cancelledAt: {
        name: '주문취소일시',
        width: 142,
        column: 'cancelledAt',
        render: (item) => {
            const cancelledAt = item.cancelledAt
                ? dayjs(item.cancelledAt).format('YYYY.MM.DD HH:mm:ss')
                : ''
            return renderChangedByInfo(cancelledAt, item.changedByNickname)
        },
    },
    refundHoldAt: {
        name: '취소접수일시',
        column: 'refundHoldAt',
        width: 200,
        render: (item, _, original) => {
            const status = original?.status
            // 취소, 반품 접수 상태 값 일 경우에만 체크박스 추가
            const refundHoldAt = item.refundHoldAt
                ? dayjs(item.refundHoldAt).format('YYYY.MM.DD HH:mm:ss')
                : ''
            if (status === 'REFUND_HOLD_CANCEL' || status === 'REFUND_HOLD_RETURN') {
                return (
                    <div className="flex flex-row gap-2 items-center">
                        <Checkbox
                            checked={original?.selectedOrders.includes(original?.order.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    original?.handleRowAction(
                                        'checkbox-item-add-group',
                                        original.order as IDetailOrder
                                    )
                                } else {
                                    original?.handleRowAction(
                                        'checkbox-item-remove-group',
                                        original.order as IDetailOrder
                                    )
                                }
                            }}
                        />
                        {renderChangedByInfo(refundHoldAt, item.changedByNickname)}
                    </div>
                )
            } else {
                return renderChangedByInfo(refundHoldAt, item.changedByNickname)
            }
        },
    },
    refundProcess: {
        name: '취소처리',
        column: 'refundProcess',
        render: (item, _, original) => {
            return (
                <div className="flex flex-row items-center gap-2">
                    <Button
                        ghost
                        small
                        onClick={() => original?.handleRowAction('cancel-start', original.order)}
                        className="!text-[#242424]"
                    >
                        환불진행
                    </Button>
                    {/* // 직접 환불 완료시 그룹화 되어있는 것을 찾아서 해당 그룹화만 처리해야함. */}
                    <Button
                        ghost
                        small
                        onClick={() => original?.handleRowAction('refund-complete', original.order)}
                        className="!text-[#242424]"
                    >
                        직접환불완료
                    </Button>
                </div>
            )
        },
    },
    refundedAt: {
        name: '취소/반품완료일시',
        column: 'refundedAt',
        width: 142,

        render: (item) => {
            const refundedAt = item.refundedAt
                ? dayjs(item.refundedAt).format('YYYY.MM.DD HH:mm:ss')
                : ''
            return renderChangedByInfo(refundedAt, item.changedByNickname)
        },
    },
    orderedAt: {
        name: '주문일시',
        column: 'orderedAt',
        width: 142,
        render: (item) => {
            return item.orderedAt ? dayjs(item.orderedAt).format('YYYY.MM.DD HH:mm:ss') : ''
        },
    },
    status: {
        name: '주문상태',
        column: 'status',
        width: 142,
        render: (item) => {
            return <>{OrderStatusChangeKr(item.status)}</>
        },
    },
    statusUpdatedAt: {
        name: '상태변경일시',
        column: 'statusUpdatedAt',
        width: 142,
        render: (item) => {
            return item.statusUpdatedAt
                ? dayjs(item.statusUpdatedAt).format('YYYY.MM.DD HH:mm:ss')
                : dayjs(item.updatedAt).format('YYYY.MM.DD HH:mm:ss')
        },
    },
    trackingNumber: {
        name: '송장번호',
        column: 'trackingNumber',
        width: 142,
        render: (item, _, original) => {
            const status = original?.status
            if (status === 'TOTAL') {
                return <p className="">{item.trackingNumber ?? '-'}</p>
            }
            return (
                <button
                    type="button"
                    className="flex flex-row items-center gap-1 pr-2"
                    onClick={() => {
                        original && original.handleRowAction('editTracking', original.todo)
                    }}
                >
                    <p className="text-left">{item.trackingNumber}</p>
                    <LucideIcon name="Pencil" size={12} className="text-[#007AFF] min-w-[16px]" />
                </button>
            )
        },
    },
    paymentDueDate: {
        name: '결제연장기한',
        column: 'paymentDueDate',
        render: (item) => {
            return item.paymentDueDate
                ? dayjs(item.paymentDueDate).format('YYYY.MM.DD HH:mm:ss')
                : ''
        },
        width: 142,
    },
    sellerMemo: {
        name: '판매자 메모',
        column: 'sellerMemo',
        width: 400,
        render: (item, _, original) => {
            const status = original?.status
            const memo = item.sellerMemo && item.sellerMemo !== '-' ? item.sellerMemo : ' '
            if (status === 'TOTAL') {
                return <p className="">{memo}</p>
            }
            return (
                <button
                    type="button"
                    className="flex flex-row items-center gap-1 pr-2 max-w-[400px]"
                    onClick={() => {
                        original && original.handleRowAction('editMemo', original.todo)
                    }}
                >
                    <p className="text-left">{memo}</p>
                    <LucideIcon name="Pencil" size={12} className="text-[#007AFF] min-w-[16px]" />
                </button>
            )
        },
    },

    // 결제 금액
    actualPaidAmount: {
        name: '실결제금액',
        column: 'actualPaidAmount',
        width: 142,
        render: (item, _, original) => {
            const __ = original?.todo.payment as Payment | null
            let fee = __?.totalAmount ?? 0
            return <>{fee.toLocaleString()}</>
        },
    },
    actualRefundAmount: {
        name: '실환불금액',
        column: 'actualRefundAmount',
        width: 142,
        render: (item, _, original) => {
            const __ = original?.todo.payment as Payment | null
            // console.log(__)
            let fee = (__?.totalAmount ?? 0) - (__?.balanceAmount ?? 0)
            return <>{fee.toLocaleString()}</>
        },
    },
    finalPaidAmount: {
        name: '최종결제금액',
        column: 'finalPaidAmount',
        width: 142,
        render: (item, _, original) => {
            const __ = original?.todo.payment as Payment | null
            let totalProductPrice = __?.balanceAmount ?? 0
            return numbericComma(totalProductPrice)
        },
    },
    additionalFee: {
        name: '추가배송비',
        width: 142,
        column: 'additionalFee',
        render: (item, _, original) => {
            return numbericComma(Number(original?.order.refundAddShippingFee ?? 0))
        },
    },
    returnFee: {
        name: '반품배송비',
        width: 142,
        column: 'returnFee',
        render: (item, _, original) => {
            return numbericComma(Number(original?.order.refundShippingFee ?? 0))
        },
    },
    expectedAmount: {
        name: '결제예정금액',
        column: 'expectedAmount',
        width: 142,
        render: (item, _, original) => {
            const totalProductPrice = Number(original?.todo.totalPayment ?? 0)
            const fee = getShippingFee({
                shippingType: original?.shippingInfo.shippingType ?? 'PAID_SHIPPING',
                shippingPurchaseAmount: original?.shippingInfo.shippingPurchaseAmount ?? 0,
                shippingCharge: original?.shippingInfo.shippingCharge ?? 0,
                totalProductPrice,
                remoteAreaShippingCharge: original?.todo.remoteAreaShippingCharge ?? 0,
                jejuShippingCharge: original?.todo.jejuShippingCharge ?? 0,
                isTotal: true,
            })

            // console.log(totalProductPrice, fee)

            return numbericComma(totalProductPrice + fee)
        },
    },
}

const renderChangedByInfo = (time: string, changedByNickname: string) => {
    return (
        <div className="flex flex-col text-center">
            <span>{time}</span>
            {changedByNickname ? <span>{changedByNickname}</span> : null}
        </div>
    )
}

export const StatusTable: React.FC<StatusTableProps> = ({
    broadcastId,
    status,
    data = [],
    shippingInfo,
    scrollRef,
    handleScroll,
    handleRowAction,
    selectedOrders,
    isAllSelected,
}) => {
    const fields: IField[] = getFieldsByStatus(status, shippingInfo)
    // const rows: TableRow[] = mapTodosToRows(status, data)
    let columnZIndex = 50

    const rows = useMemo(() => {
        return mapTodosToRows(status, data)
    }, [status, data])

    // console.log('rows', rows)

    return (
        // 1) overflow-x-auto: 가로 스크롤 허용
        //    h-full: 세로도 꽉 채우되, 필요하면 세로 스크롤은 컨테이너가 알아서
        <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-x-auto h-full bg-white -mt-2"
        >
            {/* 
          2) table-auto: 콘텐츠만큼 컬럼 폭을 유연하게 잡고
             min-w-max: content width 가 container width 보다 크면 스크롤
        */}
            <table
                className={classNameJoin([
                    'table-auto min-w-max border-collapse',
                    'text-[12px] md:text-[13px] text-[#242424]',
                    'relative',
                ])}
            >
                <thead className="relative z-[5]">
                    <tr>
                        {fields.map((f) => {
                            let Children = <>{f.name}</>
                            // 컬럼별 특별한 렌더링 로직
                            if (f.column === 'checkbox') {
                                Children = (
                                    <Checkbox
                                        checked={isAllSelected}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handleRowAction(
                                                    'checkbox-user-all',
                                                    {} as IDetailTodo
                                                )
                                            } else {
                                                handleRowAction(
                                                    'checkbox-user-all-remove',
                                                    {} as IDetailTodo
                                                )
                                            }
                                        }}
                                    />
                                )
                            } else if (f.column === 'receiverPhone') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>수령인 연락처</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                고객이 정산서에서 배송정보(이름,
                                                                연락처, 주소)를 직접 변경할 수
                                                                있습니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                단, 주문 중 1개라도 [배송준비] 또는
                                                                [배송완료] 상태가 되면 고객은 더
                                                                이상 배송정보를 변경할 수 없습니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                extraOffset={10}
                                                width={443}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'shippingFee') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>배송비</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                고객이 결제 시 지불한 기본
                                                                배송비입니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                해당 라이브에 설정된 배송비가
                                                                적용되며, 주문 전체 취소 시
                                                                환불됩니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                width={443}
                                                extraOffset={10}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'additionalFee') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>추가배송비</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                구매자 사유로 부분 취소 시 무료배송
                                                                조건을 충족하지 못한 경우, 고객이
                                                                추가로 부담하는 배송비입니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                환불 금액에서 차감되어 환불됩니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                width={480}
                                                extraOffset={10}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'returnFee') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>반품배송비</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                구매자 사유로 부분 반품 시, 고객이
                                                                부담하는 배송비입니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                무료배송 조건 충족 여부에 따라 환불
                                                                금액에서 편도 또는 왕복 배송비가
                                                                차감되어 환불됩니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                width={480}
                                                extraOffset={10}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'finalPaidAmount') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>최종결제금액</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                고객이 결제한 실결제금액에서
                                                                실환불금액을 제외한 최종 금액입니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                width={450}
                                                extraOffset={11}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'actualPaidAmount') {
                                Children = (
                                    <div className="flex flex-row items-center gap-2">
                                        <p>{f.name}</p>
                                        <div className="translate-y-1">
                                            <TooltipComponent
                                                label={' '}
                                                tooltipText={
                                                    <>
                                                        <p>실결제금액</p>
                                                        <ul>
                                                            <li className="!text-left">
                                                                고객이 최초 결제 시 실제로 결제한
                                                                전체 금액입니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                총상품금액의 합 + 배송비 +
                                                                제주/도서산간 배송비를 포함한
                                                                금액입니다.
                                                            </li>
                                                            <li className="!text-left">
                                                                전체 주문 취소시 환불될
                                                                환불예정금액과 동일합니다.
                                                            </li>
                                                        </ul>
                                                    </>
                                                }
                                                tooltipPosition="bottom"
                                                textAlign="left"
                                                width={480}
                                                extraOffset={11}
                                            />
                                        </div>
                                    </div>
                                )
                            } else if (f.column === 'refundProcess') {
                                let text = '취소처리'
                                if (status === 'REFUND_HOLD_RETURN') {
                                    text = '반품처리'
                                } else if (status === 'REFUND') {
                                    text = '환불처리'
                                }
                                Children = <p>{text}</p>
                            } else if (f.column === 'cancelReason') {
                                let text = '취소사유'
                                if (status === 'REFUND_HOLD_RETURN') {
                                    text = '반품사유'
                                } else if (status === 'REFUND') {
                                    text = '환불사유'
                                }
                                Children = <p>{text}</p>
                            } else if (f.column === 'cancelDetailReason') {
                                let text = '취소상세사유'
                                if (status === 'REFUND_HOLD_RETURN') {
                                    text = '반품상세사유'
                                } else if (status === 'REFUND') {
                                    text = '환불상세사유'
                                }
                                Children = <p>{text}</p>
                            } else if (f.column === 'refundHoldAt') {
                                let text = '취소접수일시'
                                if (status === 'REFUND_HOLD_RETURN') {
                                    text = '반품접수일시'
                                }
                                Children = <p>{text}</p>
                            }
                            if (!f.column) return null
                            return (
                                <th
                                    key={f.column}
                                    className={classNameJoin([
                                        'sticky top-0 p-2 border-r-2 border-[#F5F5F5] text-left bg-[#F5F5F5] pl-3 font-semibold break-keep',
                                        f.align || '',
                                    ])}
                                    style={{
                                        width: f.width ?? '240px',
                                        zIndex: columnZIndex,
                                    }}
                                >
                                    {Children}
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((rowObj, rowIndex) => {
                        // console.log('fields', fields)
                        return (
                            <tr key={rowIndex} className={'bg-white border border-[#f5f5f5]'}>
                                {fields.map((f) => {
                                    const span = rowObj.rowSpan[f.column] || 0
                                    // console.log('f', f, span)
                                    if (span === 0) return null
                                    return (
                                        <td
                                            key={f.column}
                                            rowSpan={span > 1 ? span : undefined}
                                            className={classNameJoin([
                                                'p-2 border-[#f5f5f5] border-r-2 pl-3',
                                                f.align || '',
                                            ])}
                                        >
                                            {f.render
                                                ? f.render(rowObj.values, rowIndex, {
                                                      ...rowObj.original,
                                                      status,
                                                      broadcastId,
                                                      shippingInfo,
                                                      handleRowAction,
                                                      selectedOrders,
                                                  })
                                                : rowObj.values[f.column]}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}

                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={fields.length} className="p-4 text-center text-gray-500">
                                데이터가 없습니다.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}
